import { computed, inject, Injectable, resource, signal } from '@angular/core';

import { getOwnerFullName } from '../site-owner/utils/owner.util';

import { LinkRequestsGateway } from './link-requests.gateway';
import { LinkRequest, LinkRequestOID } from './models/LinkRequest';
import type { LinkRequestCommand } from './models/LinkRequestCommand';
import { LinkRequestVm } from './models/linkRequestVm';

/**
 * The installer's pending link requests, shown in the « Demandes en attente » tab and counted on the dashboard.
 * Nothing is loaded until the dashboard asks; the state lives as long as the page: logging out leaves the
 * application, so there is no session reset.
 */
@Injectable({ providedIn: 'root' })
export class LinkRequestsStore {
    private readonly gateway = inject(LinkRequestsGateway);

    /** The last list loaded, with the changes confirmed since: a failed load keeps it. */
    private readonly linkRequests = signal<LinkRequest[]>([]);

    /**
     * Undefined until the list is first asked for, which keeps the resource idle. Bumped to ask again even while a
     * load runs.
     */
    private readonly generation = signal<number | undefined>(undefined);
    private readonly state = resource({
        params: this.generation,
        loader: async ({ abortSignal }) => {
            const linkRequests = await this.gateway.getAll();
            // Aborted when a newer request or a change overtook this one: its answer may predate the change.
            if (!abortSignal.aborted) this.linkRequests.set(linkRequests);
        },
    });

    /** True while the list is asked to the backend, reloads included. */
    readonly loading = this.state.isLoading;

    readonly failed = computed(() => this.state.status() === 'error');

    /** Newest first. Empty until a list arrives. */
    readonly items = computed<LinkRequestVm[]>(() =>
        this.linkRequests()
            .map((linkRequest) => ({ ...linkRequest, siteOwners: linkRequest.siteOwners.map(getOwnerFullName) }))
            .sort((a, b) => b.creationTime - a.creationTime),
    );

    readonly count = computed(() => this.linkRequests().length);

    /** Loads the list the first time; later calls keep it. */
    load(): void {
        this.generation.update((generation) => generation ?? 0);
    }

    /** Asks the backend again, keeping the list shown meanwhile. Does nothing while a load is in flight. */
    reload(): void {
        this.load();
        this.state.reload();
    }

    async create(command: LinkRequestCommand): Promise<void> {
        const linkRequest = await this.gateway.create(command);
        this.reconcile((linkRequests) => upsert(linkRequests, linkRequest));
    }

    /** The resent request is replaced by the new one the backend answers with. */
    async resend(linkRequestOID: LinkRequestOID): Promise<void> {
        const linkRequest = await this.gateway.resend(linkRequestOID);
        this.reconcile((linkRequests) => upsert(without(linkRequests, linkRequestOID), linkRequest));
    }

    async remove(linkRequestOID: LinkRequestOID): Promise<void> {
        await this.gateway.remove(linkRequestOID);
        this.reconcile((linkRequests) => without(linkRequests, linkRequestOID));
    }

    /**
     * Applies a change the backend confirmed to the list shown. A load in flight may answer with a list sent before
     * the change, and a failed one left the list stale: either way, asks again. Never asked for, there is no list to
     * change: the first load brings the change.
     */
    private reconcile(change: (linkRequests: LinkRequest[]) => LinkRequest[]): void {
        if (this.generation() === undefined) return;

        this.linkRequests.update(change);
        if (this.state.isLoading() || this.failed()) {
            this.generation.update((generation) => (generation ?? 0) + 1);
        }
    }
}

/** A load answered after the backend made the change may already hold the request: it is not shown twice. */
function upsert(linkRequests: LinkRequest[], linkRequest: LinkRequest): LinkRequest[] {
    return [...without(linkRequests, linkRequest.linkRequestOID), linkRequest];
}

function without(linkRequests: LinkRequest[], linkRequestOID: LinkRequestOID): LinkRequest[] {
    return linkRequests.filter((linkRequest) => linkRequest.linkRequestOID !== linkRequestOID);
}
